/**
 * Owner: Masa Bayu
 * File: CancelAccountPanel.tsx
 * Date:Time: 2026-10-22 13:20
 * Description: §457 F6-C — zon "Batalkan akaun" (§453) pada kad Akaun /account.
 *   Re-auth kata laluan; ralat hub (401/403/404/409) diterjemah di sempadan
 *   papar (hub-error-copy), bukan ditulis semula di sini. Akaun Google-sahaja
 *   TIDAK diberi borang: `verifyAccountPassword` di hub pasti 401 kerana
 *   `passHash` kosong (diukur), jadi jurang S3 dipaparkan sebagai nota — bukan
 *   butang yang dijamin gagal.
 */

import { useState, type ReactElement } from 'react';
import { cancelHubAccount } from '../auth/hub-account-cancel';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import type { ReaderProfileLabels } from './reader-profile-labels';

export interface CancelAccountPanelProps {
  readonly labels: ReaderProfileLabels;
  /** `null` = tiada sesi; `false` = akaun Google-sahaja (S3); `true` = borang. */
  readonly hasPassword: boolean | null;
  readonly onCancelled: () => void;
}

export function CancelAccountPanel({
  labels,
  hasPassword,
  onCancelled,
}: CancelAccountPanelProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const submit = (): void => {
    if (pending || password.length === 0) return;
    setPending(true);
    setError('');
    void cancelHubAccount(password).then((attempt) => {
      if (attempt.ok) {
        onCancelled();
        return;
      }
      setPending(false);
      setPassword('');
      setError(attempt.error);
    });
  };

  return (
    <section className="qxr__danger" aria-labelledby="qxr-danger">
      <h3 id="qxr-danger" className="qxr__danger-title">
        <LandingIcon name="lock" size={16} /> {labels.cancelHeading}
      </h3>
      {hasPassword === null ? (
        <p className="qxr__danger-note">{labels.cancelNoSession}</p>
      ) : hasPassword ? (
        <>
          <p className="qxr__danger-note">{labels.cancelIntro}</p>
          <p className="qxr__danger-note">{labels.cancelOrdersNote}</p>
          {open ? (
            <form
              className="qxr__danger-form"
              onSubmit={(event) => {
                // Tanpa preventDefault, pelayar menghantar borang secara GET dan
                // reload /account — DELETE ke hub dibatalkan di tengah jalan.
                // (Dijsdom ini tidak kelihatan: requestSubmit tidak dibina.)
                event.preventDefault();
                submit();
              }}
            >
              <label className="qxr__danger-label" htmlFor="qxr-cancel-pw">
                {labels.cancelPasswordLabel}
              </label>
              <input
                id="qxr-cancel-pw"
                className="qxr__danger-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              {error !== '' ? (
                <p className="qxr__danger-error" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="qxr__danger-actions">
                <button
                  type="submit"
                  className="qxr__cta qxr__cta--danger"
                  disabled={pending || password.length === 0}
                >
                  {pending ? labels.cancelWorking : labels.cancelConfirm}
                </button>
                <button
                  type="button"
                  className="qxr__cta qxr__cta--ghost"
                  disabled={pending}
                  onClick={() => {
                    setOpen(false);
                    setError('');
                    setPassword('');
                  }}
                >
                  {labels.cancelDismiss}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className="qxr__cta qxr__cta--danger qxr__danger-open"
              onClick={() => {
                setOpen(true);
              }}
            >
              {labels.cancelButton}
            </button>
          )}
        </>
      ) : (
        <p className="qxr__danger-note">{labels.cancelNoPassword}</p>
      )}
    </section>
  );
}
